import React, { useState } from 'react'
import { Answer as AnswerType, AnswerQuestion as AnswerQuestionType } from '@/types/admin'

interface AnswerProps {
  answer: AnswerType
  answerNumber: number
}

const Answer: React.FC<AnswerProps> = ({ answer, answerNumber }) => {
  const [isOpen, setIsOpen] = useState(true)

  const disabilityDescriptions: Record<string, string> = {
    visual: "Discapacidad visual",
    hearing: "Discapacidad auditiva",
    motor: "Discapacidad motora",
    cognitive: "Discapacidad cognitiva"
  };

  const disabilityIcon = (
    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
      <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="2" />
    </svg>
  );

  return (
    <div className="bg-white rounded-lg shadow-md p-6  mb-4">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center text-left">
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold">Respuesta N°{answerNumber}</h1>
              <p className="text-sm text-gray-500">
                {new Date(answer.created_at).toLocaleString('es-ES', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                })}
                h
              </p>
            </div>
            { (answer.email || answer.phone) && (
              <div className="flex items-center space-x-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
                <span>Quiere contacto</span>
              </div>
            )}
          </div>
        </div>
        <svg
          className={`w-6 h-6 transform transition-transform ml-4 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="mt-6">
          <div className="mb-8">
            <div className="bg-gray-700 text-white rounded-t-lg p-3">
              <h2 className="text-lg font-semibold">Preguntas</h2>
            </div>
            <div className="bg-white p-4">
              <div className="grid grid-cols-1 gap-4">
                {answer.answer_questions.map((aq: AnswerQuestionType) => (
                  <div key={aq.id} className="border border-gray-200 rounded-lg p-4">
                    <p className="font-semibold mb-2">
                      {aq.question.position + 1}. {aq.question.body}
                    </p>
                    <p className="text-gray-700">{aq.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

            {(answer.email || answer.phone || answer.has_disability) && (
              <div className="mb-4">
                <div className="bg-blue-600 text-white rounded-t-lg p-3">
                  <h2 className="text-lg font-semibold">Información Adicional</h2>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-b-lg p-4 space-y-3">
                  { answer.has_disability && answer.disability_type && (
                    <div className="flex items-center space-x-2">
                      {disabilityIcon}
                      <span className="text-blue-800 font-medium">Discapacidad:&nbsp;</span>
                      {disabilityDescriptions[answer.disability_type] || answer.disability_type}
                    </div>
                  )}
                  {answer.email && (
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                      <span className="text-blue-800 font-medium">Email de contacto:</span>
                      <a 
                        href={`mailto:${answer.email}`} 
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        {answer.email}
                      </a>
                    </div>
                  )}
                  {answer.phone && (
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span className="text-blue-800 font-medium">Teléfono de contacto:</span>
                      <a 
                        href={`tel:${answer.phone}`} 
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        {answer.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  )
}

export default Answer
